<script lang="ts">
import { FileUrl, useRedirections } from "@/composable/applications/useFunction";
import useText from "@/composable/components/text";
import { computed, onMounted, ref } from "vue";
import services from "@/composable/services";
import PageView from "@/views/common/PageView.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import useArray from "@/composable/components/array";
import i18n from "@/i18n";
import { useRouter } from "vue-router";
import useObject from "@/composable/components/object";
import useBoolean from "@/composable/components/boolean";
import DetailApplicationModalCard from "@/components/application/DetailApplicationModalCard.vue";
import "@/style/views/application/ApplicationInfoView.scss";
import CardAccessSectionApplication from "@/components/application/CardAccessSectionApplication.vue";
import { BButton, BField, BIcon, BInput, BMessage, BModal, BTabItem, BUpload } from "buefy";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import BundleReport from "@/components/datas/report/BundleReport.vue";
import { useResultProgress } from "@/composable/application/bundlReportComposable";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { CreateUserRequest } from "@/types/generated/UsedGeneratedTypes";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { hasAnyApplicationAccess } from "@/composable/authorization/applicationAccessGuard";
import { isCharteSigned } from "@/composable/applications/charteSignState";
import UnauthorizedApplicationAccessPanel from "@/components/common/UnauthorizedApplicationAccessPanel.vue";

export default {
  name: "ApplicationInfoView",
  components: {
    BTabItem,
    LoadingAnimate,
    BIcon,
    BButton,
    BInput,
    BField,
    BUpload,
    BModal,
    CardAccessSectionApplication,
    DetailApplicationModalCard,
    SubMenu,
    PageView,
    BundleReport,
    UnauthorizedApplicationAccessPanel,
  },
  props: {
    applicationName: {
      type: String,
      default: "",
    },
  },
  emits: ["setValidatedCharte"],
  setup(props, ctx) {
    let chunkFunctions = ref(useResultProgress(new LocalApplicationResult(), "fr"));
    const router = useRouter();
    const isCardModalActive = useBoolean().refBoolean;
    const url = useText().refText;
    const chartComment = ref("");
    const showCommentModal = ref(false);
    const redirection = ref(() => {
      router.push(`/applications/${props.applicationName}`);
    });
    const showUploadZip = ref(false);
    const onFileSelected = function () {
      console.log(tmpCharte);
      showCommentModal.value = true;
    };
    const confirmComment = async function () {
      showCommentModal.value = false;
      await uploadCharteApplication(application, chartComment.value);
      // Réinitialise les champs si besoin
      chartComment.value = "";
    };
    const showCharte = async function (objectShowChartePromise: Promise<FileUrl | void>) {
      if (!objectShowChartePromise) {
        return;
      }
      const objectShowCharte = await objectShowChartePromise;
      if (!objectShowCharte) {
        return;
      }
      isCardModalActive.value = true;
      url.value = objectShowCharte.url;
      if (objectShowCharte.redirection) {
        redirection.value = objectShowCharte.redirection;
      }
    };
    const validated = computed({
      get() {
        return hasSignedLastCharte();
      },
      set(bool) {
        const storedAuthenticatedUser = services.loginService.getStoredAuthenticatedUser();
        if (bool) {
          const createUserRequest = {} as CreateUserRequest;
          createUserRequest.login = storedAuthenticatedUser?.login || "";
          createUserRequest.email = storedAuthenticatedUser?.email || "";
          createUserRequest.charte = application.id;
          services.loginService.modifAcount(createUserRequest);
          ctx.emit("setValidatedCharte", {
            applicationId: application.id,
            validated: bool,
          });
        }
      },
    });
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const { refBoolean: canCreateApplication, doChangeBoolean: changeCanCreateApplication }
      = useBoolean(false);
    const { refBoolean: canManagerApplication, doChangeBoolean: changeCanManagerApplication }
      = useBoolean(false);
    const { refBoolean: canManagerUser, doChangeBoolean: changeCanManagerUser } = useBoolean(false);
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();

    const {
      createApplication,
      updateApplication,
      downloadApplicationUploadBundle,
      downloadApplicationUploadBundleWithData,
      downloadYamlApplication,
      downloadCharteApplication,
      uploadCharteApplication,
      tmpCharte,
      showReferenceRights,
      displayAdditionalFilesManagement,
      showAdditionalFilesManagementRights,
      showRequestRights,
      landingPageApplication,
      zipFile,
      onZipSelected,
    } = useRedirections(application);
    const canReadRef = ref(false);
    const canReadData = ref(false);

    // Wrapper function to pass chunkFunctions to onZipSelected
    const handleZipSelected = () => {
      if (!chunkFunctions.value) {
        return;
      }
      if (!chunkFunctions.value.parseChunck) {
        return;
      }
      onZipSelected(chunkFunctions.value);
    };
    const showProgressModal = computed({
      get() {
        return chunkFunctions.value?.showProgress || false;
      },
      set(value) {
        if (chunkFunctions.value) {
          chunkFunctions.value.showProgress = value;
        }
      },
    });
    const canReadReferences = computed(() =>
      Object.keys(application.references).filter(
        reference => application?.authorizations?.[reference]?.READ,
      ),
    );
    const canReadDataTypes = computed(() =>
      Object.keys(application.dataTypes).filter(
        dataType => application?.authorizations?.[dataType]?.READ,
      ),
    );
    const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean();

    onMounted(async () => {
      changeIsLoading(true);
      await init();
      canReadRef.value
        = Object.keys(application.references).filter(
          reference => application?.authorizations?.[reference]?.READ,
        ).length > 0;
      canReadData.value
        = Object.keys(application.dataTypes).filter(
          dataType => application?.authorizations?.[dataType]?.READ,
        ).length > 0;
      changeSubMenuPaths([
        new SubMenuPath(
          "Accueil",
          () => router.push(`/applications/${props.applicationName}`),
          () => {
          },
        ),
      ]);
      changeIsLoading(false);
    });

    async function init() {
      try {
        // Audit OA_FULL_REVIEW (8/5/26) : on ne demande PAS REFERENCETYPE ici.
        // Côté backend, ce filtre déclenche getReferenceSynthesis qui exécute
        // "SELECT ReferenceType, COUNT(*) FROM <schema>.referencevalue
        // GROUP BY ReferenceType" (~6 s sur si_acbb 10M rows). Or la page
        // d'accueil application n'utilise pas application.referenceSynthesis ;
        // seule DatasManagementView le consomme et fait son propre fetch
        // dédié avec REFERENCETYPE quand l'utilisateur navigue vers Données.
        const fetchedApplication = await services.applicationService.getApplication(props.applicationName, [
          "DATATYPE",
          "CONFIGURATION",
          "ADDITIONALFILE",
        ]);
        if (fetchedApplication) {
          // #466 - Si le backend renvoie id/name vides , on retombe sur le
          // param de route Vue qui est toujours renseigné. Garantit un
          // identifiant stable pour toutes les opérations downstream
          // ( signature charte , lookup localStorage , construction d'URL… ).
          fetchedApplication.id = fetchedApplication.id || props.applicationName;
          fetchedApplication.name = fetchedApplication.name || props.applicationName;
          fetchedApplication.localRefName = services.internationalisationService.localeReferenceNames(
            props.applicationName,
            fetchedApplication,
          ) || "";
          changeApplication(fetchedApplication);
          // Garde-fou : doChangeObject ( composable/components/object.ts )
          // supprime les clés dont la valeur n'a pas changé entre deux
          // appels , ce qui vide 'id' et 'name' au 2eme appel de init()
          // ( après signature de charte par exemple ) . On ré-affirme
          // ici les deux propriétés à partir de {@code fetchedApplication}
          // ( qui détient toujours les valeurs résolues , potentiellement
          // un vrai UUID retourné par le backend ) , et PAS depuis
          // {@code application.id} qui peut être vide après strip et
          // retomberait alors sur {@code applicationName} ( perte de
          // l'UUID -> doublon de clé en base au 2e signage de charte ) .
          application.id = fetchedApplication.id || props.applicationName;
          application.name = fetchedApplication.name || props.applicationName;
        }
      }
      catch (_e) {
        services.alertService.toastError(
          i18n.global.t("alert.server-error-appli-no-exist", {
            name: props.applicationName,
          }),
        );
      }
      try {
        changeCanCreateApplication(
          application?.currentApplicationUserRolesResult?.isApplicationCreator
            ? application?.currentApplicationUserRolesResult?.isApplicationCreator
            : application?.currentApplicationUserRolesResult?.isOpenAdomAdmin,
        );
        chunkFunctions.value = useResultProgress(application);
        changeCanManagerApplication(
          application?.currentApplicationUserRolesResult?.applicationRoles?.includes(
            "applicationManager",
          ),
        );
        changeCanManagerUser(
          application?.currentApplicationUserRolesResult?.applicationRoles?.includes("userManager"),
        );
        if (
          !canCreateApplication.value
          && !canManagerApplication.value
          && !hasSignedLastCharte()
        ) {
          const fileUrlPromise = landingPageApplication(application, () => {
            isCardModalActive.value = false;
            init(); // Ferme la modale au lieu de rediriger
          });
          showCharte(
            fileUrlPromise,
          );
        }
      }
      catch (error) {
        console.log("missing admin application rights", error);
      }
    }

    function hasSignedLastCharte(): boolean {
      // Check de présence aligné sur {@code mustSignCharte()} ( cf
      // GenericLocalApplicationResult.ts ) via le helper pur
      // {@code isCharteSigned} . L'ancienne comparaison
      // {@code now > timestamp} renvoyait faux juste après une
      // signature fraîche ( skew horloge client / serveur ) , forçant
      // l'utilisateur à signer 2 fois et générant un doublon en base
      // ( clé name ET id ) quand l'identifiant oscillait pendant
      // l'hydratation . Bug audit .
      return isCharteSigned(
        services.loginService.getStoredAuthenticatedUser(),
        application.id,
        application.name,
      );
    }

    /**
     * Vrai si l'application expose un mécanisme de demande de droits
     * ( bouton "Demander des accès aux données" sur la carte ) ; dans
     * ce cas l'utilisateur sans rôle doit pouvoir interagir avec la
     * page pour soumettre sa demande - on ne lui présente PAS le
     * placeholder "accès non autorisé" .
     */
    const hasRightsRequestFlow = computed<boolean>(() => {
      const cfg = application?.configuration?.rightsRequest;
      return !!cfg && Object.keys(cfg).length > 0;
    });

    /**
     * Vrai si l'utilisateur connecté peut interagir avec la page :
     * <ul>
     *   <li>il possède au moins un rôle ( ou est openAdomAdmin global ) , OU</li>
     *   <li>l'application propose un workflow de demande de droits ,
     *       ce qui permet à un utilisateur sans rôle de cliquer sur le
     *       bouton "Demander des accès aux données" pour démarrer le
     *       flow ( signature de charte + formulaire ) .</li>
     * </ul>
     * Quand faux , la vue affiche un placeholder explicite via
     * {@link UnauthorizedApplicationAccessPanel} au lieu de la
     * description + onglets , évitant les sous-routes qui plantaient
     * en HTTP 500 ( cf {@code AuthorizationFilter} sous RLS ) .
     */
    const hasApplicationAccess = computed<boolean>(() => {
      if (hasAnyApplicationAccess(application?.currentApplicationUserRolesResult ?? null)) {
        return true;
      }
      return hasRightsRequestFlow.value;
    });

    /** Login affiché dans le placeholder d'accès refusé . */
    const currentUserLogin = computed<string>(() =>
      services.loginService.getStoredAuthenticatedUser()?.login ?? "",
    );

    /**
     * Vrai si l'utilisateur doit signer la charte de l'application pour
     * pouvoir interagir avec ses cartes . Pilote l'affichage de la
     * bannière de récupération sous les cartes ( cf retour UX :
     * fermeture accidentelle de la modale charte = impasse silencieuse ) .
     * On lit le state via {@code hasSignedLastCharte} déjà défini ,
     * et on ne montre la bannière que si l'application expose un
     * workflow de droits ( sans rights-request , il n'y a rien à
     * débloquer ici ) .
     */
    const needsCharteSign = computed<boolean>(() => {
      if (!hasRightsRequestFlow.value) return false;
      return !hasSignedLastCharte();
    });

    /**
     * Relance le flow d'affichage de la charte ( même fonction utilisée
     * à l'arrivée sur la page ) . Utilisé par la bannière de récupération
     * pour permettre à l'utilisateur de ré-ouvrir la modale sans
     * recharger la page .
     */
    function reopenCharte(): void {
      showCharte(landingPageApplication(application, () => {
        isCardModalActive.value = false;
        init();
      }));
    }

    return {
      hasApplicationAccess,
      currentUserLogin,
      needsCharteSign,
      reopenCharte,
      canReadReferences,
      canReadDataTypes,
      createApplication,
      updateApplication,
      downloadApplicationUploadBundle,
      downloadApplicationUploadBundleWithData,
      downloadYamlApplication,
      downloadCharteApplication,
      uploadCharteApplication,
      tmpCharte,
      validated,
      application,
      showReferenceRights,
      showRequestRights,
      displayAdditionalFilesManagement,
      showAdditionalFilesManagementRights,
      subMenuPaths,
      canCreateApplication,
      showCharte,
      isCardModalActive,
      canManagerUser,
      canManagerApplication,
      url,
      redirection,
      canReadData,
      canReadRef,
      showCommentModal,
      onFileSelected,
      chartComment,
      confirmComment,
      isLoading,
      zipFile,
      onZipSelected,
      handleZipSelected,
      showUploadZip,
      chunkFunctions,
      showProgressModal,
    };
  },
};
</script>

<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :is-accueil="true"
      :paths="subMenuPaths"
      :root="application.localName || applicationName"
      role="navigation"
    />
    <h1 class="title main-title">
      {{ application.localName || applicationName }}
    </h1>
    <b-modal
      v-model="showProgressModal"
      hide-footer
      centered
      title="Importation des fichiers"
      class="showProgress"
    >
      <BundleReport
        v-if="chunkFunctions"
        :chunk="chunkFunctions"
      />
    </b-modal>
    <b-modal
      v-if="isCardModalActive"
      v-model="isCardModalActive"
      class="modalByAgrégation"
      full-screen
    >
      <DetailApplicationModalCard
        :id="application.name"
        v-model="isCardModalActive"
        :application="application"
        :close-cb="redirection"
        :open="isCardModalActive"
        :redirection="redirection"
        :url="url"
      />
    </b-modal>
    <b-modal
      v-if="showCommentModal"
      v-model="showCommentModal"
      :can-cancel="['escape', 'x']"
    >
      <div class="box">
        <b-upload
          v-model="tmpCharte"
          :validation-message="$t('applications.functions.file-select')"
          accept=".pdf"
          class="file-label"
          required
          @input="onFileSelected"
        >
          <span class="file-cta">
            <b-icon
              class="file-icon"
              icon="pen-square"
            />
            <span class="file-label">{{ $t("applications.functions.chose-charter-file") }}</span>
          </span>
        </b-upload>
        <b-field :label="$t('dataTypesRepository.comment')">
          <b-input
            v-model="chartComment"
            :placeholder="$t('dataTypesRepository.placeholder-comment')"
            maxlength="255"
            @keyup.enter="confirmComment"
          />
        </b-field>
        <div class="card-footer">
          <div class="card-footer-item">
            <b-button
              expanded
              type="is-danger"
              @click="showCommentModal = false"
            >
              {{ $t("alert.cancel") }}
            </b-button>
          </div>
          <div class="card-footer-item">
            <b-button
              expanded
              type="is-primary"
              @click="confirmComment"
            >
              {{ $t("dataTypesManagement.validate") }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
    <LoadingAnimate
      v-if="isLoading"
      :size="'is-large'"
    />
    <UnauthorizedApplicationAccessPanel
      v-else-if="!hasApplicationAccess"
      :application-name="application.localName || applicationName"
      :user-login="currentUserLogin"
    />
    <div
      v-else
      class="contentView main-content"
    >
      <div class="content-description">
        <div class="infos">
          <p
            :aria-label="
              $t('applications.version', {
                applicationName: application.localName,
                version: application?.configuration?.applicationDescription?.version?.version,
              })"
            v-html="
              $t('applications.version', {
                applicationName: application.localName,
                version: application?.configuration?.applicationDescription?.version?.version,
              })
            "
          />
        </div>
        <div class="infos">
          <p v-if="application.comment">
            <span
              :aria-label="$t('applications.comment')"
              class="has-text-primary"
            >
              <B>{{ $t("applications.comment") }}</B>
            </span>
            <span
              :aria-label="application.comment"
              class="comment"
            >{{ application.comment }}</span>
          </p>
          <p v-else>
            <span
              :aria-label="$t('applications.no-comment')"
              class="has-text-warning"
            >
              <B>{{ $t("applications.no-comment") }}</B>
            </span>
          </p>
        </div>
        <div>
          <p v-if="application.localDescription">
            <span
              :aria-label="$t('applications.description')"
              class="has-text-primary"
            >
              <B>{{ $t("applications.description") }}</B>
            </span>
            <span
              :aria-label="application.localDescription"
            >
              {{ application.localDescription }}
            </span>
          </p>
        </div>
      </div>
      <div
        v-if="canCreateApplication || canManagerApplication || canManagerUser"
        class="card-home-gestion-app"
      >
        <b-tabs
          class="buttonRubriquePanel"
          position="is-centered"
        >
          <b-tab-item
            v-if="application.data && Object.keys(application.data).length !== 0"
            :aria-label="$t('applications.functions.datas-manage')"
            aria-description="data"
            class="columns "
          >
            <template #header>
              <span class="rubriqueTitle">{{ $t("applications.functions.datas-manage") }}</span>
            </template>
            <CardAccessSectionApplication
              v-if="application.references && Object.keys(application.references).length !== 0"
              :param-btn-click="application"
              :title-card="$t('applications.references')"
              btn-function-name="displayReferencesManagement"
              btn-icon="drafting-compass"
              btn-type="is-dark"
              :description-card="$t('applications.references_description')"
            />
            <CardAccessSectionApplication
              v-if="application.dataTypes && Object.keys(application.dataTypes).length !== 0"
              :param-btn-click="application"
              :title-card="$t('applications.dataset')"
              btn-function-name="displayDataSetManagement"
              btn-icon="poll"
              btn-type="is-dark"
              :description-card="$t('applications.dataset_description')"
            />
            <CardAccessSectionApplication
              :btn-outlined="true"
              :param-btn-click="application"
              :title-card="$t('applications.functions.datas-rights')"
              btn-function-name="showDataRights"
              btn-icon="key"
              btn-type="is-warning"
              :description-card="$t('applications.datas-rights_description')"
            />
          </b-tab-item>
          <!-- TODO - en attente des spec des fichiers complémentaires
          <b-tab-item
              v-if="
              application.additionalFiles && Object.keys(application.additionalFiles).length !== 0
            "
              aria-description="additional files"
              disabled
          >
            <template #header>
              <span class="rubriqueTitle">{{
                  $t("applications.functions.additional-files-manage")
                }}</span>
            </template>
            <CardAccessSectionApplication
                btn-icon="file"
                :param-btn-click="application"
                description-card="à faire"
                :title-card="$t('applications.additionalFile')"
                btn-function-name="displayAdditionalFilesManagement"
                btn-type="is-dark"
            />
            <CardAccessSectionApplication
                v-if="canCreateApplication || canManagerApplication"
                btn-icon="file"
                :param-btn-click="application"
                description-card="à faire"
                :title-card="$t('applications.functions.additional-files-rights')"
                btn-function-name="showAdditionalFilesManagementRights"
                btn-type="is-dark"
            />
          </b-tab-item>-->
          <b-tab-item
            v-if="canCreateApplication || canManagerApplication"
            :aria-label="$t('applications.functions.application-manage')"
            aria-description="application"
          >
            <template #header>
              <span class="rubriqueTitle">{{
                $t("applications.functions.application-manage")
              }}</span>
            </template>
            <!--
              Rangée 1 : 3 cartes ( config / mise à jour SI / kit publication ).
              Custom class `app-management-row-cards` ( cf. ApplicationInfoView.scss )
              qui garantit explicitement un flex centré + width 16rem par carte ,
              SANS dépendance à l'ordre de cascade des styles Bulma. Les classes
              Bulma `column is-3` étaient fragiles : `is-3` était écrasé par
              `width: 16rem !important` de .card-btn-section , et selon l'ordre
              d'import SCSS le centrage `is-centered` pouvait ne pas s'appliquer.
            -->
            <span class="app-management-row-cards">
              <CardAccessSectionApplication
                :param-btn-click="application"
                :title-card="$t('applications.config-file')"
                btn-function-name="downloadYamlApplication"
                btn-icon="download"
                btn-type="is-dark"
                :description-card="$t('applications.config-file_description')"
              />
              <CardAccessSectionApplication
                v-if="canCreateApplication && canManagerApplication"
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('applications.change')"
                btn-function-name="updateApplication"
                btn-icon="pen-square"
                btn-type="is-warning"
                :description-card="$t('applications.change_description')"
              />
              <CardAccessSectionApplication
                v-if="canCreateApplication && canManagerApplication"
                :param-btn-click="application"
                :title-card="$t('applications.downloadBundle')"
                btn-function-name="downloadApplicationUploadBundle"
                btn-icon="toolbox"
                btn-type="is-primary"
                :description-card="$t('applications.downloadBundle_description')"
              />
            </span>
            <span class="columns">
              <CardAccessSectionApplication
                v-if="canCreateApplication && canManagerApplication"
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('applications.downloadBundleWithData')"
                btn-function-name="downloadApplicationUploadBundleWithData"
                btn-icon="hard-drive"
                btn-type="is-dark"
                class="column is-offset-2 is-4"
                :description-card="$t('applications.downloadBundleWithData_description')"
              />
              <CardAccessSectionApplication
                v-if="canCreateApplication && canManagerApplication"
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('applications.functions.uploadBundle')"
                btn-function-name="onZipSelected(chunk)"
                btn-icon="window-restore"
                btn-type="is-primary"
                class="is-4"
                :description-card="$t('applications.uploadBundle_description')"
              >
                <template #action>
                  <b-upload
                    v-model="zipFile"
                    :validation-message="$t('applications.functions.file-select')"
                    expanded
                    outlined
                    required
                    type="is-primary"
                    @update:model-value="handleZipSelected"
                  >
                    <p class="button is-warning is-fullwidth is-outlined">
                      <b-icon icon="window-restore" />
                    </p>
                  </b-upload>
                </template>
              </CardAccessSectionApplication>
            </span>
            <span class="columns">
              <CardAccessSectionApplication
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('applications.functions.charte-consult')"
                btn-function-name="downloadCharteApplication"
                btn-icon="eye"
                btn-type="is-dark"
                class="column is-offset-2 is-4"
                :description-card="$t('applications.charte-consult_description')"
                @modal-value-changes="showCharte($event)"
              />
              <CardAccessSectionApplication
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('applications.functions.upload')"
                btn-function-name="showCommentModal"
                btn-icon="file-pen"
                btn-type="is-warning"
                class="column is-4"
                :description-card="$t('applications.upload_description')"
                @show-modal="showCommentModal = $event"
              />
            </span>

          </b-tab-item>
          <b-tab-item
            :aria-label="$t('applications.functions.right-requests-manage')"
            aria-description="authorization"
          >
            <template #header>
              <span class="rubriqueTitle">{{
                $t("applications.functions.right-requests-manage")
              }}</span>
            </template>
            <span class="columns">
              <CardAccessSectionApplication
                :param-btn-click="application"
                :title-card="$t('applications.request')"
                btn-function-name="requestRights"
                btn-icon="users-cog"
                btn-type="is-dark"
                :description-card="$t('applications.request_description')"
              />
              <CardAccessSectionApplication
                :btn-outlined="true"
                :param-btn-click="application"
                :title-card="$t('dataTypeAuthorizations.delegation-management-right')"
                btn-function-name="authorizationsManagementForApplicationUsers"
                btn-icon="shield-halved"
                btn-type="is-warning"
                :description-card="$t('applications.delegation-management-right_description')"
              />
            </span>
          </b-tab-item>
        </b-tabs>
      </div>
      <div
        v-else
        class="card-home-app"
      >
        <div class="columns">
          <CardAccessSectionApplication
            v-if="application.references && Object.keys(application.references).length !== 0"
            :param-btn-click="application"
            :title-card="$t('applications.references')"
            btn-function-name="displayReferencesManagement"
            btn-icon="drafting-compass"
            btn-type="is-dark"
            :description-card="$t('applications.references_description')"
          />
          <CardAccessSectionApplication
            v-if="application.dataTypes && Object.keys(application.dataTypes).length !== 0"
            :param-btn-click="application"
            :title-card="$t('applications.dataset')"
            btn-function-name="displayDataSetManagement"
            btn-icon="poll"
            btn-type="is-dark"
            :description-card="$t('applications.dataset_description')"
            @modal-value-changes="showCharte"
          />
          <CardAccessSectionApplication
            v-if="
              application?.configuration &&
                application?.configuration.rightsRequest &&
                Object.keys(application?.configuration.rightsRequest).length !== 0
            "
            :btn-outlined="true"
            :param-btn-click="application"
            :title-card="$t('dataTypeAuthorizations.showRequests')"
            btn-function-name="showRequestRights"
            btn-icon="users-cog"
            btn-type="is-primary"
            :description-card="$t('applications.showRequests_description')"
            @modal-value-changes="showCharte"
          />
          <!--TODO: additionalFile
                      <div
                          v-if="
                        application.additionalFiles && Object.keys(application.additionalFiles).length !== 0
                      "
                          class="buttonWarper"
                      >
                        <b-button
                            disabled
                            icon-left="file"
                            @click="showCharte(displayAdditionalFilesManagement(application))"
                        >
                          {{ $t("applications.additionalFile") }}
                        </b-button>
                      </div>-->
        </div>
        <!--
          Bannière de récupération quand l'utilisateur a fermé la
          modale charte sans la signer ( cf retour UX : impasse car
          les clics sur les cartes ouvraient bien la modale mais sa
          fermeture sans signature laissait la page muette ) .
          Affichée uniquement quand la charte est requise ET non signée ;
          l'unique action proposée relance le flow d'affichage via
          {@code landingPageApplication} + {@code showCharte} ,
          identique au flow d'arrivée sur la page .
        -->
        <div
          v-if="needsCharteSign"
          class="charte-banner"
          role="alert"
          aria-live="polite"
        >
          <b-icon
            icon="circle-exclamation"
            size="is-small"
            type="is-warning"
          />
          <span class="charte-banner__text">
            {{ $t("applications.charte-required-banner") }}
          </span>
          <b-button
            type="is-primary"
            size="is-small"
            icon-left="file-signature"
            @click="reopenCharte"
          >
            {{ $t("applications.charte-required-banner-action") }}
          </b-button>
        </div>
      </div>
    </div>
  </PageView>
</template>
